import type { ComponentType, ReactNode } from 'react';
import {
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

import { colors, fonts, fontSizes, radii, spacing } from '@/constants/theme';

export type PillTone =
  | 'green'
  | 'greenSoft'
  | 'dark'
  | 'blue'
  | 'warn'
  | 'coral'
  | 'outline'
  | 'outlineOnDark';
type Tone = PillTone;

type IconProps = { color: string; size: number };

export interface PillProps {
  children: ReactNode;
  tone?: Tone;
  /** Solid dot indicator before the label, in the label's color. */
  dot?: boolean;
  icon?: ComponentType<IconProps>;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  testID?: string;
}

/**
 * Status pill / badge. Variants codify the brand kit's sanctioned color
 * combos (page 11) — never freelance a tone+text-color pair, that's the
 * "Cool Blue + Electric Green" forbidden-pair class of bug.
 */
export function Pill({
  children,
  tone = 'green',
  dot,
  icon: Icon,
  style,
  accessibilityLabel,
  testID,
}: PillProps) {
  const palette = TONES[tone];
  return (
    <View
      style={[styles.base, palette.container, style]}
      accessibilityLabel={accessibilityLabel}
      testID={testID}
    >
      {dot && <View style={[styles.dot, { backgroundColor: palette.text.color }]} />}
      {Icon && <Icon color={palette.text.color} size={10} />}
      <Text style={[styles.text, palette.text]} numberOfLines={1}>
        {children}
      </Text>
    </View>
  );
}

const TONES: Record<Tone, { container: ViewStyle; text: TextStyle & { color: string } }> = {
  green: {
    container: { backgroundColor: colors.electricGreen },
    text: { color: colors.deepGreen },
  },
  greenSoft: {
    container: { backgroundColor: 'rgba(120, 193, 68, 0.18)' },
    text: { color: colors.deepGreen },
  },
  dark: {
    container: { backgroundColor: colors.deepGreen },
    text: { color: colors.frost },
  },
  blue: {
    container: { backgroundColor: 'rgba(108, 198, 222, 0.20)' },
    text: { color: colors.deepGreen },
  },
  warn: {
    container: { backgroundColor: '#FFF1D6' },
    text: { color: '#6B4A00' },
  },
  coral: {
    container: { backgroundColor: '#FCE0E1' },
    text: { color: '#7A1F1F' },
  },
  outline: {
    container: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.border,
    },
    text: { color: colors.deepGreen },
  },
  // `outline` for Deep Green surfaces: Frost ink and a Frost-tint rule,
  // the sanctioned pair for text on Deep Green (brand kit p.11).
  outlineOnDark: {
    container: {
      backgroundColor: 'transparent',
      borderWidth: 1,
      borderColor: colors.frostTint,
    },
    text: { color: colors.frost },
  },
};

const styles = StyleSheet.create({
  base: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs + 2,
    paddingVertical: 5,
    paddingHorizontal: 11,
    borderRadius: radii.full,
    alignSelf: 'flex-start',
  },
  text: {
    fontFamily: fonts.bodyBold,
    fontSize: fontSizes.xs - 2,
    textTransform: 'uppercase',
    letterSpacing: 1.0,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 999,
  },
});
